import React, { useState, useContext } from 'react';
import { Box, Tabs, Tab, Typography, Card, CardContent, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { AuthContext } from '../context/AuthContext';
import { Navigate, useParams } from 'react-router-dom';
import { InfoOutlined, MapOutlined, AccessTime, FilterList, ShoppingCart, DescriptionOutlined } from '@mui/icons-material';
import RestaurantInfo from './RestaurantInfo';
import QuartiersSection from './QuartiersSection';
import CategoriesSection from './CategoriesSection';
import HorairesSection from './HorairesSection';
import CommandesSection from './CommandesSection';
import FacturesSection from './FacturesSection';

// Fluid card with soft shadow and rounded corners
const StyledCard = styled(Card)(({ theme }) => ({
  margin: 'auto',
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)',
  borderRadius: 16,
  background: '#ffffff',
  marginBottom: theme.spacing(4),
  marginLeft: { xs: '2%', sm: '5%', md: '10%' }, // Percentage-based margins
  marginRight: { xs: '2%', sm: '5%', md: '10%' },
  transition: 'box-shadow 0.3s ease',
  '&:hover': {
    boxShadow: '0 6px 24px rgba(0, 0, 0, 0.1)',
  },
}));

// Clean tabs with subtle hover and active states
const StyledTabs = styled(Tabs)(({ theme }) => ({
  borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
  '& .MuiTab-root': {
    fontWeight: 500,
    color: '#475569',
    textTransform: 'none',
    fontSize: '1rem',
    padding: theme.spacing(1.5, 3),
    fontFamily: "'Inter', sans-serif",
    transition: 'color 0.2s ease', // Removed transform from transition
    '&:hover': {
      color: '#21ccbb',
      // Removed transform: 'translateY(-1px)' to prevent layout shift
    },
    '&.Mui-selected': {
      color: '#21ccbb',
      fontWeight: 600,
    },
  },
  '& .MuiTabs-indicator': {
    backgroundColor: '#21ccbb',
    height: 3,
    borderRadius: '3px 3px 0 0',
  },
  // Style the scroll buttons to prevent flickering
  '& .MuiTabs-scrollButtons': {
    color: '#475569',
    transition: 'opacity 0.3s ease, color 0.2s ease',
    '&:hover': {
      color: '#21ccbb',
    },
    '&.Mui-disabled': {
      opacity: 0.3,
    },
  },
}));

// Minimal loading container
const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '50vh',
  background: '#f8fafc',
}));

const EditRestaurant = () => {
  const { user, loading } = useContext(AuthContext);
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <LoadingContainer>
        <CircularProgress size={40} thickness={4} sx={{ color: '#21ccbb' }} />
      </LoadingContainer>
    );
  }

  const shouldRedirect = !user || !['admin', 'manager'].includes(user?.role);
  if (shouldRedirect) {
    return <Navigate to="/dashboard" />;
  }

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 4, md: 6 },
        bgcolor: '#f8fafc',
        minHeight: '100vh',
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <StyledCard>
        <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              fontWeight: 700,
              color: '#1e293b',
              mb: 4,
              fontFamily: "'Inter', sans-serif",
            }}
          >
            Modifier le Restaurant
          </Typography>
          <StyledTabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 4 }}
          >
            <Tab label="Informations" icon={<InfoOutlined sx={{ fontSize: 20 }} />} iconPosition="start" />
            <Tab label="Quartiers" icon={<MapOutlined sx={{ fontSize: 20 }} />} iconPosition="start" />
            <Tab label="Horaires" icon={<AccessTime sx={{ fontSize: 20 }} />} iconPosition="start" />
            <Tab label="Filtres" icon={<FilterList sx={{ fontSize: 20 }} />} iconPosition="start" />
            <Tab label="Commandes" icon={<ShoppingCart sx={{ fontSize: 20 }} />} iconPosition="start" />
            <Tab label="Factures" icon={<DescriptionOutlined sx={{ fontSize: 20 }} />} iconPosition="start" />
          </StyledTabs>

          <Box sx={{ minHeight: '300px' }}>
            {activeTab === 0 && <RestaurantInfo restaurantId={id} />}
            {activeTab === 1 && <QuartiersSection restaurantId={id} />}
            {activeTab === 2 && <HorairesSection restaurantId={id} />}
            {activeTab === 3 && <CategoriesSection restaurantId={id} />}
            {activeTab === 4 && <CommandesSection restaurantId={id} />}
            {activeTab === 5 && <FacturesSection restaurantId={id} />}
          </Box>
        </CardContent>
      </StyledCard>
    </Box>
  );
};

export default EditRestaurant;